export enum StaffPersona {
  AdmissionsAndIntake = 'Admissions & Intake',
  ClinicalOversight = 'Clinical Oversight',
  FacilityLeadership = 'Facility Leadership',
  MarketingAndSourceRelationshipManagement = 'Marketing & Source Relationship Management',
  BusinessOfficeAndRevenue = 'Business Office & Revenue',
  ReimbursementAndAuthorizations = 'Reimbursement & Authorizations',
  TransitionsOfCare = 'Transitions of Care',
  CorporateAndRegionalLeadership = 'Corporate & Regional Leadership',
  Other = 'Other'
}

const TITLES_BY_PERSONA: Record<StaffPersona, string[]> = {
  [StaffPersona.AdmissionsAndIntake]: [
    'Admissions Director',
    'Director of Admissions',
    'Director of Admissions and Marketing',
    'Admissions Coordinator',
    'Admissions Assistant',
    'Admissions Nurse',
    'Admissions Manager',
    'Admissions',
    'Intake Coordinator',
    'Central Intake Coordinator (CIC)',
    'Central Intake',
    'Central Intake Specialist',
    'Intake Nurse'
  ],
  [StaffPersona.ClinicalOversight]: [
    'Director of Nursing (DON)',
    'Director of Nursing Services (DNS)',
    'Regional Nurse Consultant',
    'Assistant Director of Nursing (ADON)',
    'Assistant Director of Nursing Services (ADNS)',
    'Registered Nurse (RN)',
    'Nurse Manager',
    'Unit Manager',
    'Treatment Nurse',
    'Regional Nurse',
    'Licensed Practical Nurse (LPN)',
    'Charge Nurse',
    'Licensed Vocational Nurse (LVN)',
    'Clinical Evaluator',
    'Clinical Nurse Evaluator',
    'Infection Preventionist (IP)',
    'Clinical Reviewer',
    'Quality Assurance Nurse'
  ],
  [StaffPersona.FacilityLeadership]: [
    'Administrator',
    'Executive Director (ED)',
    'Nursing Home Administrator (NHA)',
    'Administrator in Training (AIT)',
    'Assistant Administrator',
    'Licensed Nursing Home Administrator (LNHA)',
    'Assistant Executive Director',
    'General Manager'
  ],
  [StaffPersona.MarketingAndSourceRelationshipManagement]: [
    'Hospital Liaison',
    'Liaison',
    'Clinical Liaison (CL)',
    'Patient Care Liaison (PCL)',
    'Community Liaison',
    'Territory Manager',
    'Director of Business Development (DBD)',
    'Business Development Specialist (BDS)',
    'Regional Director of Business Development (RDBD)',
    'Business Development',
    'Area Director of Business Development',
    'Sales Counselor',
    'Marketing Director',
    'Marketing',
    'Marketer',
    'Director of Marketing',
    'Community Relations Coordinator'
  ],
  [StaffPersona.BusinessOfficeAndRevenue]: [
    'Business Office Manager (BOM)',
    'Assistant Business Office Manager (ABOM)',
    'Business Office Coordinator (BOC)',
    'Billing',
    'Insurance Specialist',
    'Biller',
    'Billing Specialist',
    'Controller'
  ],
  [StaffPersona.ReimbursementAndAuthorizations]: [
    'MDS Coordinator',
    'MDS Nurse',
    'Prospective Payment System (PPS) Coordinator',
    'Registered Nurse Assessment Coordinator (RNAC)'
  ],
  [StaffPersona.TransitionsOfCare]: [
    'Social Worker (SW)',
    'Social Services (SS)',
    'Social Services Director (SSD)',
    'Care Navigator',
    'Case Manager',
    'Healthcare Navigator',
    'Transition Care Specialist'
  ],
  [StaffPersona.CorporateAndRegionalLeadership]: [
    'Chief Executive Officer (CEO)',
    'Chief Operating Officer (COO)',
    'Chief Nursing Officer (CNO)',
    'Chief Financial Officer (CFO)',
    'Owner',
    'Vice President (VP)'
  ],
  [StaffPersona.Other]: [
    'Director of Rehab (DOR)',
    'Therapy Director',
    'Therapy Program Director/Manager',
    'Medical Records',
    'Health Unit Coordinator (HUC)',
    'Medical Records Coordinator',
    'Director of Customer Experience',
    'Director of Reservations',
    'Concierge',
    'Receptionist'
  ]
};

const PERSONA_BY_LOWERCASE_TITLE = new Map(
  Object.entries(TITLES_BY_PERSONA).flatMap(([persona, titles]) =>
    titles.map((title) => [title.toLowerCase(), persona as StaffPersona])
  )
);

const CANONICAL_TITLE_BY_LOWERCASE = new Map(
  Object.values(TITLES_BY_PERSONA)
    .flat()
    .map((title) => [title.toLowerCase(), title])
);

export const STANDARD_STAFF_TITLES: string[] = Object.values(TITLES_BY_PERSONA)
  .flat()
  .sort((a, b) => a.localeCompare(b));

export const OTHER_STAFF_TITLE_MIN_LENGTH = 2;
export const OTHER_STAFF_TITLE_MAX_LENGTH = 100;

export const findStandardStaffTitle = (title: string): string | undefined =>
  CANONICAL_TITLE_BY_LOWERCASE.get(title.trim().toLowerCase());

export const getStaffTitlePersona = (
  title: string | null | undefined
): StaffPersona | null =>
  (title && PERSONA_BY_LOWERCASE_TITLE.get(title.trim().toLowerCase())) || null;
